import React, { useEffect, useMemo, useState } from 'react';
import LoginPopup from '../components/LoginPopup';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from '../utils/helmet';
import { useUser } from '../context/UserContext';
import API_BASE_URL from '../config/api';
import styles from './EditProfilePage.module.css';
import { buildStorageUrl } from '../config/firebaseStorage';
import { auth, storage } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  DEFAULT_PRICE_SETTINGS,
  EXCHANGE_TARGET_CURRENCIES,
  getPriceSettings,
  PRICE_VIEW_MODES,
  savePriceSettings,
  SUPPORTED_PRICE_CURRENCIES
} from '../utils/priceSettings';

const HIDE_SETTINGS_PRICE_SECTION = false;
const HIDE_SETTINGS_EMAIL_SECTION = false;

const getFileExtension = (file) => {
  const name = String(file?.name || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  const ext = name.slice(idx).toLowerCase();
  if (!ext || ext.length > 10) return '';
  return ext;
};

function EditProfilePage() {
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [loginPopupMessage, setLoginPopupMessage] = useState('');
  const navigate = useNavigate();
  const {
    currentUser,
    updateProfile,
    updateUserPfp,
    resendVerificationEmail,
    updateGearWatchlistAlertFrequency,
    logout,
    initializing,
    firebaseUser
  } = useUser();
  const isGoogleOnly = Boolean(firebaseUser?.providerData?.length) && firebaseUser.providerData.every((provider) => provider.providerId === 'google.com');
  const { t } = useTranslation();

  const [editorialApplying, setEditorialApplying] = useState(false);

  // Local state
  const [nickname, setNickname] = useState(currentUser?.nickname || '');
  const [bio, setBio] = useState(currentUser?.bio || '');
  const [website, setWebsite] = useState(
    currentUser?.socials?.find(s => s.type === 'website')?.url || ''
  );
  const [email, setEmail] = useState(currentUser?.email || '');
  const [instagram, setInstagram] = useState(() => {
    return currentUser?.socials?.find(s => s.type === 'instagram')?.handle || '';
  });
  const [youtube, setYoutube] = useState(
    currentUser?.socials?.find(s => s.type === 'youtube')?.url || ''
  );
  const [pfpFile, setPfpFile] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [priceSettings, setPriceSettings] = useState(() => getPriceSettings());
  const [priceSaveMessage, setPriceSaveMessage] = useState('');
  const [watchlistAlertFrequency, setWatchlistAlertFrequency] = useState(currentUser?.gearWatchlistAlertFrequency || 'weekly');
  const [watchlistAlertsSaving, setWatchlistAlertsSaving] = useState(false);
  const [watchlistAlertsMessage, setWatchlistAlertsMessage] = useState('');

  useEffect(() => {
    if (!initializing && !currentUser) {
      navigate('/');
    }
  }, [initializing, currentUser, navigate]);

  useEffect(() => {
    if (!priceSaveMessage) return undefined;
    const timer = window.setTimeout(() => setPriceSaveMessage(''), 1800);
    return () => window.clearTimeout(timer);
  }, [priceSaveMessage]);

  useEffect(() => {
    setWatchlistAlertFrequency(currentUser?.gearWatchlistAlertFrequency || 'weekly');
  }, [currentUser?.gearWatchlistAlertFrequency]);

  useEffect(() => {
    if (!watchlistAlertsMessage) return undefined;
    const timer = window.setTimeout(() => setWatchlistAlertsMessage(''), 2200);
    return () => window.clearTimeout(timer);
  }, [watchlistAlertsMessage]);

  const selectedPriceCurrencySet = useMemo(
    () => new Set(priceSettings.selectedPriceCurrencies),
    [priceSettings.selectedPriceCurrencies]
  );

  const persistPriceSettings = (nextSettings) => {
    const saved = savePriceSettings(nextSettings);
    setPriceSettings(saved);
    setPriceSaveMessage('Settings saved on this device.');
  };

  const handleTogglePriceCurrency = (currency) => {
    const currentlySelected = priceSettings.selectedPriceCurrencies;
    const hasCurrency = currentlySelected.includes(currency);

    let nextSelected = hasCurrency
      ? currentlySelected.filter((entry) => entry !== currency)
      : [...currentlySelected, currency];

    if (nextSelected.length === 0) {
      nextSelected = ['EUR'];
    }

    if (nextSelected.length === 1 && nextSelected[0] !== 'EUR') {
      nextSelected = ['EUR', nextSelected[0]];
    }

    persistPriceSettings({
      ...priceSettings,
      selectedPriceCurrencies: nextSelected
    });
  };

  const handleSaveWatchlistAlerts = async () => {
    setWatchlistAlertsSaving(true);
    const result = await updateGearWatchlistAlertFrequency(watchlistAlertFrequency);
    setWatchlistAlertsSaving(false);

    if (!result?.success) {
      setWatchlistAlertsMessage(result?.message || 'Failed to save watchlist alerts.');
      return;
    }

    setWatchlistAlertsMessage('Watchlist alert settings saved.');
  };

  const handleResendVerification = async () => {
    setVerifyLoading(true);
    const result = await resendVerificationEmail();
    setVerifyLoading(false);
    if (result.success) {
      setVerifyMessage('Verification email has been sent. Please check your inbox.');
    } else {
      setVerifyMessage(result.message || 'Failed to send verification email.');
    }
  };

  const handlePfpChange = (e) => {
    const f = e.target.files?.[0];
    setPfpFile(f || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // `updateProfile` is responsible for translating these inputs into `socials`
      // and sending them to the backend.
      const payload = {
        nickname,
        bio,
        website: (website || '').trim(),
        instagram: (instagram || '').trim().replace(/^@/, ''),
        youtube: (youtube || '').trim(),
        email: (email || '').trim(),
      };

      const result = await updateProfile(payload);
      if (!result.success) {
        alert(result.message || t('editProfile.alerts.updateFailed'));
        return;
      }

      if (pfpFile) {
        const uid = auth.currentUser?.uid;
        if (!uid) {
          alert(t('editProfile.alerts.genericError'));
          return;
        }
        const extension = getFileExtension(pfpFile);
        const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}${extension}`;
        const objectPath = `pfp/uploads/${uid}/${filename}`;
        const fileRef = storageRef(storage, objectPath);
        await uploadBytes(fileRef, pfpFile, { contentType: pfpFile.type || undefined });
        const pfpUrl = await getDownloadURL(fileRef);

        const pfpRes = await updateUserPfp({ pfpUrl });
        if (!pfpRes.success) {
          alert(pfpRes.message || t('editProfile.alerts.pfpFailed'));
          return;
        }
      }

      alert(t('editProfile.alerts.updateSuccess'));
      navigate('/');
    } catch (err) {
      console.error('Edit profile error:', err);
      alert(err?.message || t('editProfile.alerts.genericError'));
    }
  };

  // Change password handler
  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdError('');
    setPwdSuccess('');

    if (!newPassword || !confirmPassword) {
      setPwdError(t('editProfile.alerts.passwordRequired'));
      alert(t('editProfile.alerts.passwordRequired'));
      return;
    }
    if (newPassword.length < 8) {
      setPwdError(t('editProfile.alerts.passwordLength'));
      alert(t('editProfile.alerts.passwordLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError(t('editProfile.alerts.passwordMismatch'));
      alert(t('editProfile.alerts.passwordMismatch'));
      return;
    }

    try {
      setPwdLoading(true);
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error(t('editProfile.alerts.passwordReauth'));
      }

      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setPwdSuccess(t('editProfile.alerts.passwordSuccess'));
      alert(t('editProfile.alerts.passwordSuccess'));
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwdError(err?.message || t('editProfile.alerts.passwordChangeFailed'));
      alert(err?.message || t('editProfile.alerts.passwordChangeFailed'));
    } finally {
      setPwdLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    const confirmed = window.confirm(t('editProfile.alerts.deleteConfirm'));
    if (!confirmed) return;

    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        // Check for re-authentication error
        if (data.message && data.message.toLowerCase().includes('recent login')) {
          setLoginPopupMessage(t('editProfile.alerts.reauthRequired'));
          setShowLoginPopup(true);
          setDeleting(false);
          return;
        }
        throw new Error(data.message || `Request failed (${res.status})`);
      }
      alert(t('editProfile.alerts.deleteSuccess'));
      logout();
      navigate('/');
    } catch (err) {
      console.error('Delete account error:', err);
      alert(t('editProfile.alerts.deleteFailed', { message: err?.message || '' }));
    } finally {
      setDeleting(false);
    }
  };

  const handleApplyForEditorial = async () => {
    if (!currentUser) return;

    setEditorialApplying(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/me/editorial-application`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Request failed (${res.status})`);
      }

      alert(t('editProfile.alerts.editorialSuccess'));
    } catch (err) {
      console.error('Apply for editorial error:', err);
      alert(err?.message || t('editProfile.alerts.editorialError'));
    } finally {
      setEditorialApplying(false);
    }
  };

  if (initializing) {
    return (
      <div className={styles.container}>
        <p>{t('editProfile.loading')}</p>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const currentPfpUrl =
    currentUser?.pfp && currentUser.pfp.trim() !== ''
      ? currentUser.pfp
      : buildStorageUrl('pfp/placeholderP.png');

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{t('common.settings', { defaultValue: 'Settings' })} · Mountains</title>
        <meta
          name="description"
          content="Manage your account and local price display settings."
        />
      </Helmet>

      {/* Header row */}
      <div className={styles.headerRow}>
        <Link to="/profile" className={`${styles.backLink} backButton`}>
          ← {t('editProfile.backLink')}
        </Link>
        <h1 className={styles.title}>{t('common.settings', { defaultValue: 'Settings' })}</h1>
      </div>

      {/* Card container */}
      <div className={styles.gridContainer}>
        {/* Left: current avatar and username */}
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <img
              src={currentPfpUrl}
              alt={t('editProfile.profileAlt', { name: currentUser?.nickname || t('editProfile.fallbackName') })}
              className={styles.pfpImage}
            />
            <div className={styles.userInfo}>
              <div className={styles.nickname}>
                {currentUser?.nickname || t('editProfile.fallbackName')}
              </div>
              <div className={styles.username}>@{currentUser?.username}</div>
            </div>
          </div>
        </div>

        {/* Right: edit form */}
        <form onSubmit={handleSubmit} className={styles.formCard}>
          <div className={styles.formGrid}>
            {/* Nickname */}
            <div>
              <label htmlFor="nickname" className={styles.label}>
                {t('editProfile.fields.nickname')}
              </label>
              <input
                id="nickname"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={t('editProfile.placeholders.nickname')}
                className={styles.input}
              />
            </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className={styles.label}>
                {t('editProfile.fields.bio')}
              </label>
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder={t('editProfile.placeholders.bio')}
                rows={4}
                className={styles.textarea}
              />
            </div>

            {/* Socials */}
            <div>
              <label htmlFor="website" className={styles.label}>
                {t('editProfile.fields.website')}
              </label>
              <input
                id="website"
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder={t('editProfile.placeholders.website')}
                className={styles.input}
              />
            </div>

            <div>
              <label htmlFor="email" className={styles.label}>
                {t('editProfile.fields.emailOptional')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('editProfile.placeholders.email')}
                className={styles.input}
                disabled={isGoogleOnly}
              />
              {isGoogleOnly && (
                <p style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)', marginTop: '0.35rem' }}>
                  {t('editProfile.messages.googleEmail')}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="instagram" className={styles.label}>
                {t('editProfile.fields.instagram')}
              </label>
              <input
                id="instagram"
                type="text"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder={t('editProfile.placeholders.instagram')}
                className={styles.input}
              />
            </div>

            <div>
              <label htmlFor="youtube" className={styles.label}>
                {t('editProfile.fields.youtube')}
              </label>
              <input
                id="youtube"
                type="url"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                placeholder={t('editProfile.placeholders.youtube')}
                className={styles.input}
              />
            </div>

            {/* Profile picture upload */}
            <div>
              <label htmlFor="pfp" className={styles.label}>
                {t('editProfile.fields.profilePicture')}
              </label>
              <input
                id="pfp"
                type="file"
                accept="image/*"
                onChange={handlePfpChange}
                className={styles.fileInput}
              />
              {pfpFile && (
                <div className={styles.fileHint}>
                  {t('editProfile.messages.fileSelected', { name: pfpFile.name })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className={styles.actions}>
              <button
                type="button"
                onClick={() => navigate('/')}
                className={styles.btnGhost}
              >
                {t('editProfile.buttons.cancel')}
              </button>
              <button
                type="submit"
                className={styles.btnPrimary}
              >
                {t('editProfile.buttons.save')}
              </button>
            </div>

            {/* Editorial application (hide for admins/editors) */}
            {!currentUser?.admin && !currentUser?.editor && (
              <div className={styles.actions}>
                <button
                  type="button"
                  onClick={handleApplyForEditorial}
                  className={styles.btnPrimary}
                  disabled={editorialApplying}
                >
                  {editorialApplying ? t('editProfile.buttons.applying') : t('editProfile.buttons.apply')}
                </button>
              </div>
            )}
          </div>
        </form>

        {currentUser && !currentUser.isVerified && (
          <div className={styles.deleteCard}>
            <h2 className={styles.deleteTitle}>Verify Your Email</h2>
            <p className={styles.deleteText}>
              Your email address is not verified. Please verify your email to unlock all features like Messaging and Team Up.
            </p>
            {verifyMessage && (
              <p className={styles.deleteText} style={{ fontWeight: 'bold' }}>{verifyMessage}</p>
            )}
            <div className={styles.actions}>
              <button
                type="button"
                onClick={handleResendVerification}
                className={styles.btnPrimary}
                disabled={verifyLoading}
              >
                {verifyLoading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </div>
          </div>
        )}

        {!HIDE_SETTINGS_PRICE_SECTION && (
          <div className={styles.deleteCard}>
            <h2 className={styles.deleteTitle}>Price Settings</h2>
            <p className={styles.deleteText}>
              Choose either original prices or exchanged estimates. The two modes are exclusive.
            </p>

            <div className={styles.formGrid}>
              <div>
                <label className={styles.label}>Display Mode</label>
                <div className={styles.settingsChoiceRow}>
                  <label className={styles.settingsChoiceCard}>
                    <input
                      type="radio"
                      name="viewMode"
                      checked={priceSettings.viewMode === PRICE_VIEW_MODES.PRICES}
                      onChange={() => persistPriceSettings({ ...priceSettings, viewMode: PRICE_VIEW_MODES.PRICES })}
                    />
                    <span>Original prices</span>
                  </label>

                  <label className={styles.settingsChoiceCard}>
                    <input
                      type="radio"
                      name="viewMode"
                      checked={priceSettings.viewMode === PRICE_VIEW_MODES.EXCHANGE}
                      onChange={() => persistPriceSettings({ ...priceSettings, viewMode: PRICE_VIEW_MODES.EXCHANGE })}
                    />
                    <span>Exchange prices</span>
                  </label>
                </div>
              </div>

              {priceSettings.viewMode === PRICE_VIEW_MODES.PRICES ? (
                <div>
                  <label className={styles.label}>Currencies to show (original prices)</label>
                  <p className={styles.settingsHint}>
                    EUR, CHF and GBP are available here. EUR is always kept when only one currency remains.
                  </p>
                  <div className={styles.settingsChoiceRow}>
                    {SUPPORTED_PRICE_CURRENCIES.map((currency) => (
                      <label key={currency} className={styles.settingsChoiceCard}>
                        <input
                          type="checkbox"
                          checked={selectedPriceCurrencySet.has(currency)}
                          onChange={() => handleTogglePriceCurrency(currency)}
                        />
                        <span>{currency}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label htmlFor="exchangeCurrency" className={styles.label}>Target exchange currency</label>
                  <p className={styles.settingsHint}>
                    Available conversion targets are aligned to current audience locales plus USD.
                  </p>
                  <select
                    id="exchangeCurrency"
                    className={styles.input}
                    value={priceSettings.exchangeTargetCurrency}
                    onChange={(event) => persistPriceSettings({ ...priceSettings, exchangeTargetCurrency: event.target.value })}
                  >
                    {EXCHANGE_TARGET_CURRENCIES.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <p className={styles.settingsHint}>
                Exchange values are estimates for comparison. Rates are cached for efficiency and refreshed weekly.
              </p>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => persistPriceSettings(DEFAULT_PRICE_SETTINGS)}
                >
                  Reset to defaults
                </button>
                {priceSaveMessage && <span className={styles.settingsSaved}>{priceSaveMessage}</span>}
              </div>
            </div>
          </div>
        )}

        {!HIDE_SETTINGS_EMAIL_SECTION && (
          <div className={styles.deleteCard}>
            <h2 className={styles.deleteTitle}>Watchlist Price Warnings</h2>
            <p className={styles.deleteText}>
              Choose how often we should email you when your watched gear prices change.
            </p>
            <div className={styles.formGrid}>
              <div>
                <label htmlFor="watchlistAlerts" className={styles.label}>Email frequency</label>
                <select
                  id="watchlistAlerts"
                  className={styles.input}
                  value={watchlistAlertFrequency}
                  onChange={(event) => setWatchlistAlertFrequency(event.target.value)}
                >
                  <option value="weekly">Weekly summary</option>
                  <option value="disabled">Disabled</option>
                  <option value="every_change">Every price change</option>
                  <option value="significant_10">Significant changes only (at least 10%)</option>
                </select>
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={handleSaveWatchlistAlerts}
                  disabled={watchlistAlertsSaving}
                >
                  {watchlistAlertsSaving ? 'Saving…' : 'Save watchlist alerts'}
                </button>
                {watchlistAlertsMessage && <span className={styles.settingsSaved}>{watchlistAlertsMessage}</span>}
              </div>
            </div>
          </div>
        )}

        {/* ----------------- Change Password box (same width as form column) ------------------ */}
        {isGoogleOnly ? (
          <div className={styles.deleteCard}>
            <h2 className={styles.deleteTitle}>{t('editProfile.password.title')}</h2>
            <p className={styles.deleteText}>
              {t('editProfile.password.googleInfo')}
            </p>
          </div>
        ) : (
          <div className={styles.deleteCard}>
            <h2 className={styles.deleteTitle}>{t('editProfile.password.title')}</h2>
            {pwdError && (
              <p className={styles.deleteText}>{pwdError}</p>
            )}
            {pwdSuccess && (
              <p className={styles.deleteText}>{pwdSuccess}</p>
            )}
            <form onSubmit={handleChangePasswordSubmit} className={styles.formGrid}>
              <div>
                <label htmlFor="oldPassword" className={styles.label}>{t('editProfile.password.current')}</label>
                <input
                  id="oldPassword"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className={styles.input}
                />
              </div>
              <div>
                <label htmlFor="newPassword" className={styles.label}>{t('editProfile.password.new')}</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={styles.label}>{t('editProfile.password.confirm')}</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.actions}>
                <button type="submit" className={styles.btnPrimary} disabled={pwdLoading}>
                  {pwdLoading ? t('editProfile.buttons.updatingPassword') : t('editProfile.buttons.updatePassword')}
                </button>
              </div>
            </form>
          </div>
        )}
        {/* ----------------- Delete account box (same width as form column) ------------------ */}
        <div className={styles.deleteCard}>
          <h2 className={styles.deleteTitle}>{t('editProfile.delete.title')}</h2>
          <p className={styles.deleteText}>
            {t('editProfile.delete.body')}
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleDeleteAccount}
              disabled={deleting}
              className={styles.btnDanger}
            >
              {deleting ? t('editProfile.buttons.deletingAccount') : t('editProfile.buttons.deleteAccount')}
            </button>
          </div>
        </div>
        {/* Show login popup for re-authentication if needed */}
        {showLoginPopup && (
          <LoginPopup
            isOpen={showLoginPopup}
            onClose={() => setShowLoginPopup(false)}
            explanation={loginPopupMessage || t('editProfile.alerts.reauthRequired')}
          />
        )}
      </div>
    </div>
  );
}

export default EditProfilePage;
